import { useRef } from 'react'
import { useLocation } from 'wouter'

import { HorizontalScroll } from '@/components/HorizontalScroll'
import { Button } from '@/components/Button'
import { CreateLinkInput } from '@/components/CreateLinkInput'
import { useForm, ValidationRules } from '@/hooks/useForm'
import { useCreateLink } from '@/hooks/useCreateLink'
import { useToast } from '@/hooks/Toast'
import { useUser } from '@/hooks/useUser'
import { destinationValidation, slugValidation } from '@/utils/validations'
import { EnterIcon } from '@/icons'
import { suggestions } from './suggestions'

import s from './Welcome.module.css'

export type FormValues = {
  slug: string
  destinations: string[]
}

const validationRules: ValidationRules = {
  slug: slugValidation,
  destinations: {
    dynamicValidation: destinationValidation,
  },
}

export const Welcome = () => {
  const { user } = useUser()
  const formRef = useRef<HTMLFormElement>(null)
  const createLinkMutation = useCreateLink()
  const toast = useToast()
  const [, setLocation] = useLocation()

  const {
    values,
    errors,
    setFieldValue,
    isSubmitting,
    handleChange,
    handleDynamicChange,
    addDynamicField,
    removeDynamicField,
    handleSubmit,
  } = useForm({
    initialValues: {
      slug: '',
      destinations: [''],
      type: 'personal',
    } as FormValues,
    validationRules: validationRules,
    formRef: formRef,
  })

  const onSubmit = async (formValues: FormValues) => {
    const urls = formValues.destinations

    const duplicates = urls.filter((url, i) => urls.indexOf(url) !== i)
    if (duplicates.length > 0) {
      toast.error('Please remove duplicate destination URLs.')
      return
    }

    const cleanedUrls = urls.map((url) => {
      const hasProtocol =
        url.startsWith('https://') || url.startsWith('http://')
      return hasProtocol ? url : `https://${url}`
    })

    let _data: any = {
      slug: formValues.slug,
      destinations: cleanedUrls,
      type: user?.organizationId ? 'organization' : 'personal',
    }

    if (user?.organizationId) {
      _data = {
        ..._data,
        organizationId: user.organizationId,
      }
    }

    try {
      await createLinkMutation.mutateAsync(_data)
      setLocation('/')
    } catch {
      toast.error('Something went wrong. Try again?')
    }
  }

  return (
    <div className={s.container}>
      <h1 className={s.title}>Create your first short link</h1>
      <h2 className={s.lede}>Select from suggestions</h2>

      <HorizontalScroll className={s.scrollContainer}>
        <div className={s.suggestions}>
          {suggestions.map((suggestion) => {
            return (
              <div key={suggestion.title} className={s.suggestion}>
                <h4 className={s.suggestionTitle}>{suggestion.title}</h4>
                <div className={s.pills}>
                  {suggestion.shortlinks.map((shortlink) => {
                    return (
                      <span
                        key={shortlink}
                        className={s.pill}
                        role="button"
                        onClick={() => {
                          setFieldValue('slug', shortlink)
                        }}
                      >
                        g.to/{shortlink}
                      </span>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </HorizontalScroll>

      <h2 className={s.lede}>Or enter any shortcut</h2>
      <form
        ref={formRef}
        className={s.form}
        noValidate
        onSubmit={handleSubmit((values) => onSubmit(values))}
      >
        <CreateLinkInput
          onChange={handleChange}
          onUrlChange={handleDynamicChange}
          errors={errors}
          values={values}
          addDynamicField={addDynamicField}
          removeDynamicField={removeDynamicField}
        />

        <div className={s.buttonContainer}>
          <Button
            variant="primary"
            type="submit"
            iconRight={EnterIcon}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create short link'}
          </Button>
          <Button
            variant="ghost"
            type="button"
            iconRight={EnterIcon}
            disabled={isSubmitting}
            onClick={() => setLocation('/')}
          >
            Skip
          </Button>
        </div>
      </form>
    </div>
  )
}
